/**
 * FavoriteToggle Component
 *
 * A reusable star button component for toggling favorite status of menu items.
 * Features smooth animations, accessibility support, and multiple size variants.
 */

import * as React from "react";
import { Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { FavoriteMenuItem } from "@/types/favorites";

/**
 * Props interface for FavoriteToggle component
 */
export interface FavoriteToggleProps {
  /** The menu item to favorite */
  item: FavoriteMenuItem;
  /** Controlled state indicating if item is favorited */
  isFavorited: boolean;
  /** Callback function when toggle is clicked */
  onToggle: (item: FavoriteMenuItem) => void;
  /** Size variant for the button */
  size?: "sm" | "md" | "lg";
  /** Additional CSS classes */
  className?: string;
  /** Disabled state */
  disabled?: boolean;
  /** Show "Favoritar" label text */
  showLabel?: boolean;
}

/**
 * Size mappings for button and icon
 */
const SIZE_CONFIG = {
  sm: {
    button: "h-8 w-8",
    icon: 14,
  },
  md: {
    button: "h-10 w-10",
    icon: 16,
  },
  lg: {
    button: "h-12 w-12",
    icon: 20,
  },
} as const;

/**
 * FavoriteToggle - Interactive star button for favoriting menu items
 *
 * @example
 * ```tsx
 * <FavoriteToggle
 *   item={menuItem}
 *   isFavorited={isFavorite(menuItem.id)}
 *   onToggle={toggleFavorite}
 *   size="md"
 * />
 * ```
 */
export const FavoriteToggle = React.memo<FavoriteToggleProps>(
  ({
    item,
    isFavorited,
    onToggle,
    size = "md",
    className,
    disabled = false,
    showLabel = false,
  }) => {
    // Animation state
    const [isAnimating, setIsAnimating] = React.useState(false);
    const [isHovered, setIsHovered] = React.useState(false);

    /**
     * Handle click event with animation lock to prevent double-clicks
     */
    const handleClick = React.useCallback(
      (e: React.MouseEvent<HTMLButtonElement>) => {
        // Prevent event bubbling to parent elements
        e.preventDefault();
        e.stopPropagation();

        // Prevent double-clicks during animation
        if (isAnimating || disabled) {
          return;
        }

        // Set animation state
        setIsAnimating(true);

        // Call toggle callback
        onToggle(item);

        // Reset animation state after animation duration
        setTimeout(() => {
          setIsAnimating(false);
        }, 300);
      },
      [item, onToggle, isAnimating, disabled]
    );

    /**
     * Handle mouse enter for hover effects
     */
    const handleMouseEnter = React.useCallback(() => {
      setIsHovered(true);
    }, []);

    /**
     * Handle mouse leave to remove hover effects
     */
    const handleMouseLeave = React.useCallback(() => {
      setIsHovered(false);
    }, []);

    // Get size configuration
    const sizeConfig = SIZE_CONFIG[size];

    // Compute dynamic aria-label
    const ariaLabel = isFavorited
      ? `Remover ${item.label} dos favoritos`
      : `Adicionar ${item.label} aos favoritos`;

    return (
      <Button
        variant="ghost"
        className={cn(
          sizeConfig.button,
          "rounded-smooth p-0 transition-all duration-300 ease-in-out",
          "hover:bg-accent/50",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
          isAnimating && "scale-110",
          className
        )}
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        disabled={disabled || isAnimating}
        aria-label={ariaLabel}
        title={ariaLabel}
        type="button"
        role="button"
        tabIndex={0}
      >
        <span className="flex items-center gap-2">
          <Star
            size={sizeConfig.icon}
            fill={isFavorited ? "currentColor" : "none"}
            strokeWidth={2}
            className={cn(
              "transition-all duration-300 ease-in-out",
              // Color states
              isFavorited
                ? "text-yellow-500"
                : "text-muted-foreground hover:text-foreground dark:text-muted-foreground dark:hover:text-muted-foreground",
              // Animation: pulse on toggle with motion-safe guard
              isAnimating && "motion-safe:animate-star-pulse motion-reduce:scale-105",
              // Hover effect (only when not animating)
              isHovered && !isAnimating && "scale-110 motion-safe:scale-110",
              // Disabled state
              disabled && "opacity-50"
            )}
          />
          {showLabel && (
            <span
              className={cn(
                "text-sm font-medium transition-colors duration-200",
                isFavorited ? "text-yellow-500" : "text-foreground dark:text-muted-foreground"
              )}
            >
              Favoritar
            </span>
          )}
        </span>
      </Button>
    );
  }
);

FavoriteToggle.displayName = "FavoriteToggle";
